import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import backIcon from '@/assets/paytm/back.png'
import driverIcon from '@/assets/paytm/driver.png'
import phoneIcon from '@/assets/paytm/phone.png'
import shareIcon from '@/assets/paytm/forward_filled.png'
import walletIcon from '@/assets/paytm/wallet.png'
import downloadIcon from '@/assets/paytm/download.png'
import storeIcon from '@/assets/paytm/store_seller.png'
import homeIcon from '@/assets/paytm/home_selected.png'
import chevronRight from '@/assets/paytm/chevron_right.png'
import dropIcon from "@/assets/paytm/map_pin.png"
import supportIcon from "@/assets/paytm/support.png"
import rtoIcon from "@/assets/paytm/rto.png"
import trackRiderIcon from "@/assets/paytm/rider_track.png"
import pickupIcon from "@/assets/paytm/pickupLoc.png"
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { Api, copyTextToClipboard, orderStatus } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Order } from '@/lib/interfaces'
import { Loader2 } from 'lucide-react'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'

export const Route = createFileRoute('/paytm/orders/$orderId')({
    component: RouteComponent,
})

const ShowOnMap = ({ pickupLoc, dropLoc, riderLoc }: {
    pickupLoc: { lat: number, lng: number },
    dropLoc: { lat: number, lng: number },
    riderLoc?: { lat: number, lng: number }
}) => {
    return <Map
        style={{ width: '100%', height: '100%' }}
        defaultBounds={{
            north: Math.max(dropLoc.lat, pickupLoc.lat), east: Math.max(dropLoc.lng, pickupLoc.lng),
            south: Math.min(dropLoc.lat, pickupLoc.lat), west: Math.min(dropLoc.lng, pickupLoc.lng)
        }}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
        mapId={'DEMO_MAP_ID'}
    >
        <AdvancedMarker position={pickupLoc} draggable>
            <div className='bg-black flex justify-center items-center w-9 h-9 rounded-full'>
                <img src={storeIcon} className='w-6' />
            </div>
        </AdvancedMarker>
        <AdvancedMarker position={dropLoc}>
            <div className='bg-black flex justify-center items-center w-9 h-9 rounded-full'>
                <img src={homeIcon} className='w-6' />
            </div>
        </AdvancedMarker>
        {riderLoc ? <AdvancedMarker position={riderLoc}>
            <img src={trackRiderIcon} className='w-6' />
        </AdvancedMarker> : null}
    </Map>
}

const TopHeader = ({ orderId, createdAt, onBackClick, onHelpClick }: { orderId: string, createdAt: string, onBackClick: () => void, onHelpClick: () => void }) => {
    return <div className='p-4 flex items-start justify-between'>
        <div className='flex items-start'>
            <img src={backIcon} className='w-6 mr-5 mt-1' onClick={onBackClick} />
            <div>
                <p className='text-lg font-bold'>{orderId}</p>
                <p className='text-sm font-medium text-gray-500'>{dayjs(createdAt).format('D MMM YYYY | hh:mm A')}</p>
            </div>
        </div>
        <p className='text-sm font-semibold text-process-cyan' onClick={onHelpClick}>Help</p>
    </div>
}


const StatusDisplay = ({ orderState, riderName, deliveredAt }: { orderState: string, riderName: string, deliveredAt?: string }) => {
    let statusTxt = ''
    let statusSubTxt = ''

    if (orderStatus[orderState] < 4) {
        statusTxt = 'Searching for rider'
        statusSubTxt = 'Rider will be assigned shortly.'
    } else if (orderStatus[orderState] === 4) {
        statusTxt = 'Rider assigned'
        statusSubTxt = 'Your delivery partner.'
    } else if (orderStatus[orderState] > 4 && orderStatus[orderState] < 9) {
        statusSubTxt = `${riderName} is on the way with your order`
        if (orderState === 'At-pickup') {
            statusTxt = 'Arrived at pickup location'
        } else if (orderState === 'At-delivery') {
            statusTxt = 'Reached drop location'
        } else {
            statusTxt = 'Order out for delivery'
        }
    } else if (orderState === 'Order-delivered') {
        statusTxt = 'Your order was delivered 🎉'
        statusSubTxt = `Delivered at ${dayjs(deliveredAt).format('hh:mm A')}`
    } else {
        if (orderState === 'Cancelled') {
            statusTxt = 'Cancelled'
        } else {
            statusTxt = 'Order  Returned !'
        }
        statusSubTxt = 'Refund for this order will be processed within 24 hours'
    }

    return <div className='px-4 py-3 flex border bg-white rounded-2xl items-center w-full'>
        {orderStatus[orderState] > 10 ? <img src={rtoIcon} className='w-12 mr-4 shrink-0' /> : <div className='w-12 h-12 rounded-full bg-gray-300 mr-4 shrink-0' />}
        <div className='w-full1 overflow-hidden'>
            <p className='text-sm font-semibold mb-1'>{statusTxt}</p>
            <div>
                <p className='text-xs font-medium text-gray-400'>{statusSubTxt}</p>
            </div>
        </div>
    </div>
}

const RiderContact = ({ riderName, riderPhone }: { riderName: string, riderPhone: string }) => {
    return <div className='px-4 py-3 flex border bg-white rounded-2xl items-center justify-between'>
        <div className='flex items-center'>
            <img src={driverIcon} className='w-12 mr-4' />
            <div>
                <p className='text-sm font-semibold mb-1'>{riderName}</p>
                <p className='text-xs font-semibold text-gray-400'>Your delivery partner</p>
            </div>
        </div>
        <a href={`tel:${riderPhone}`}><img src={phoneIcon} className='w-6' /></a>
    </div>
}

const ShareTracking = ({ trackingUrl }: { trackingUrl: string }) => {
    return <div className='flex items-center justify-center bg-process-cyan w-full py-3.5 rounded-xl active:opacity-40' onClick={async () => {
        await copyTextToClipboard(trackingUrl)
    }}>
        <img src={shareIcon} className='w-6 mr-2' />
        <p className='text-white font-semibold select-none'>Share tracking details</p>
    </div>
}


const Otp = ({ pickupOtp, dropOtp }: { pickupOtp: string, dropOtp: string }) => {
    return <div className='flex items-center w-full justify-between'>
        <div className='px-3 py-2.5 border mr-8 rounded-xl flex-1'>
            <p className='text-sm text-gray-400 font-medium'>Pickup OTP</p>
            <p className='text-lg text-dark-midnight font-bold'>{pickupOtp || '- -'}</p>
        </div>
        <div className='px-3 py-2.5 border rounded-xl flex-1'>
            <p className='text-sm text-gray-400 font-medium'>Drop OTP</p>
            <p className='text-lg text-dark-midnight font-bold'>{dropOtp || '- -'}</p>
        </div>
    </div>
}

const OrderDetails = ({ category, orderId, amount }: { category: string, orderId: string, amount: number }) => {
    return <div className='p-4 border rounded-xl'>
        <p className='font-bold mb-2'>Order Details</p>
        <p className='text-xs text-gray-500'>Package Content</p>
        <p className='text-xs font-semibold mt-1'>{category}</p>
        <div className='flex items-center justify-between mt-2.5'>
            <div>
                <p className='text-xs text-gray-500'>Bill Number</p>
                <p className='text-xs font-semibold mt-1'>{orderId}</p>
            </div>
            <div>
                <p className='text-xs text-gray-500'>Order Amount</p>
                <p className='text-xs font-semibold mt-1'>{`₹ ${amount.toFixed(2)}`}</p>
            </div>
        </div>
    </div>
}

const PaymentDetails = ({ deliveryFee, taxes, total, orderId }: { deliveryFee: number, taxes: number, total: number, orderId: string }) => {
    return <div className='p-4 border rounded-xl'>
        <div className='flex items-center justify-between mb-4'>
            <p className='font-bold'>Payment Details</p>
            <div className='flex items-center px-4 py-2 border border-process-cyan rounded-2xl'>
                <img src={downloadIcon} className='w-4 mr-1' />
                <p className='text-xs font-semibold text-process-cyan'>Invoice</p>
            </div>
        </div>
        <div className='pt-4 pb-4 border-b border-dashed'>
            <div className='flex items-center justify-between'>
                <p className='text-xs font-medium'>Delivery Fees</p>
                <p className='text-xs font-medium'>{`₹ ${deliveryFee.toFixed(2)}`}</p>
            </div>
            <div className='flex items-center justify-between'>
                <p className='text-xs font-medium'>Taxes</p>
                <p className='text-xs font-medium'>{`₹ ${taxes.toFixed(2)}`}</p>
            </div>
            <div className='flex items-center justify-between mt-2'>
                <p className='text-sm font-semibold'>Total amount paid</p>
                <p className='text-sm font-semibold'>{`₹ ${total.toFixed(2)}`}</p>
            </div>
        </div>
        <div className='mt-4'>
            <p className='text-sm font-semibold'>Payment method</p>
            <div className='flex items-start justify-between mt-4'>
                <div className='flex items-center'>
                    <div className='flex justify-center items-center w-10 h-10 rounded-full bg-light-blue'>
                        <img src={walletIcon} className='w-6' />
                    </div>
                    <div className='ml-2'>
                        <p className='text-xs font-medium'>Wallet</p>
                        <p className='mt-1 text-xs font-semibold text-gray-400'>{`Reference Id: ${orderId}`}</p>
                    </div>
                </div>
                <p className='text-sm font-semibold'>{`₹ ${total.toFixed(2)}`}</p>
            </div>
        </div>
    </div>
}

const LocationDetails = ({ pickupAddr, dropAddr }: {
    pickupAddr: string,
    dropAddr: string,
}) => {
    return <div className='w-full border rounded-xl pl-9 py-4 pr-4 relative'>
        <div className='w-full mb-6'>
            <p className='text-xs font-medium mb-1'>Pickup Details</p>
            <textarea className='resize-none outline-none border-none w-full text-sm font-semibold' value={pickupAddr} readOnly />
        </div>
        <div>
            <p className='text-xs font-medium mb-1'>Drop Details</p>
            <textarea className='resize-none outline-none border-none w-full text-sm font-semibold' value={dropAddr} readOnly />
        </div>
        <div className="absolute h-20 left-4 top-6 border-l-2 border-dotted border-gray-300 w-10">
            <img src={pickupIcon} className="w-5 absolute -left-2.5 -top-3" />
            <img src={dropIcon} className="w-5 absolute -left-2.5 -bottom-5" />
        </div>
    </div>
}


const Attachments = ({ images }: { images: string[] }) => {
    return <div className='py-4 px-3 border rounded-xl'>
        <p className='font-bold mb-2'>Attachments</p>
        <div className='flex items-center'>
            {images.filter(e => e).map(e => {
                return <img src={e} className='w-20 mr-4' key={e} />
            })}
        </div>
    </div>
}


const HelpAndSupport = ({orderId}: {orderId: string}) => {
    return <Link to='/paytm/help' search={{ forOrder: orderId }}>
        <div className='flex items-center justify-between w-full rounded-xl px-4 py-5 border'>
            <div className='flex items-center'>
                <img src={supportIcon} className='w-8 mr-4' />
                <p>Help and support</p>
            </div>
            <img src={chevronRight} className='w-6' />
        </div>
    </Link>
}


function RouteComponent() {
    const mapObj = useMap()
    const navigate = useNavigate()
    const { orderId } = Route.useParams()
    const { token } = useStore(useShallow(state => ({ token: state.token })))
    // const maps = useMapsLibrary("maps");

    // if (maps) {
    //     const flightPlanCoordinates = [
    //         { lat: order.pickupLatitude, lng: order.pickupLongitude },
    //         { lat: order.dropLatitude, lng: order.dropLongitude }
    //     ];

    //     const flightPath = new maps.Polyline({
    //         path: flightPlanCoordinates,
    //         geodesic: true,
    //         strokeColor: "#000",
    //         strokeOpacity: 1.0,
    //         strokeWeight: 2,
    //     });

    //     flightPath.setMap(mapObj);
    // }

    const getOrderInfo = useQuery({
        queryKey: ['orderInfo', orderId],
        queryFn: () => Api<{ status: number, order: Order }>('/webui/order/info', "POST", { order: { id: orderId } }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false
    })

    const trackRider = useQuery({
        queryKey: ['trackRider', orderId],
        queryFn: () => Api<{ status: number, order: { lat: number, lng: number } }>('/webui/order/track', "POST", { order: { id: orderId } }, { token }),
        staleTime: 20 * 1000,
        refetchInterval: 20 * 1000,
        refetchOnWindowFocus: true,
        enabled: !!getOrderInfo.data?.order.networkOrderId
    })

    useEffect(() => {
        if (getOrderInfo.data) {
            mapObj?.fitBounds({
                north: Math.max(getOrderInfo.data.order.dropLatitude, getOrderInfo.data.order.pickupLatitude, trackRider.data?.order ? trackRider.data?.order.lat : 0), east: Math.max(getOrderInfo.data.order.dropLongitude, getOrderInfo.data.order.pickupLongitude, trackRider.data?.order ? trackRider.data?.order.lng : 0),
                south: Math.min(getOrderInfo.data.order.dropLatitude, getOrderInfo.data.order.pickupLatitude, trackRider.data?.order ? trackRider.data?.order.lat : 1000), west: Math.min(getOrderInfo.data.order.dropLongitude, getOrderInfo.data.order.pickupLongitude, trackRider.data?.order ? trackRider.data?.order.lng : 1000)
            })
        }
    }, [getOrderInfo.data?.order, trackRider.data?.order])

    const order = getOrderInfo.data?.order

    return <div className="fixed top-0 bottom-0 left-0 right-0 bg-white z-50">
        {order ? <>
            <TopHeader orderId={order.clientOrderId} createdAt={order.createdAt} onBackClick={() => navigate({ to: '/paytm/orders' })} onHelpClick={() => navigate({ to: `/paytm/help` })} />
            {orderStatus[order.orderState] < 9 ? <div className='w-full h-[275px] border relative flex justify-center'>
                <ShowOnMap pickupLoc={{ lat: order.pickupLatitude, lng: order.pickupLongitude }} dropLoc={{ lat: order.dropLatitude, lng: order.dropLongitude }}
                    riderLoc={trackRider.data ? { lat: trackRider.data.order.lat, lng: trackRider.data.order.lng } : undefined} />
                <div className='absolute flex -bottom-8 w-full px-4 justify-center items-center'>
                    <StatusDisplay orderState={order.orderState} riderName={order.riderName} deliveredAt={order.deliveredAt} />
                </div>
            </div> : null}
            <div className={`absolute ${orderStatus[order.orderState] < 9 ? 'top-[390px]' : 'top-[60px]'} left-0 right-0 bottom-0 px-4 pb-4 *:my-4 overflow-auto order`}>
                {orderStatus[order.orderState] >= 9 ? <>
                    <StatusDisplay orderState={order.orderState} riderName={order.riderName} deliveredAt={order.deliveredAt} />
                    <LocationDetails pickupAddr={order.pickupAddress.name + ', ' + order.pickupAddress.line1 + ', ' + order.pickupAddress.line2}
                        dropAddr={order.dropAddress.name + ', ' + order.dropAddress.line1 + ', ' + order.dropAddress.line2} />
                </> : <>
                    {order.riderName ? <RiderContact riderName={order.riderName} riderPhone={order.riderNumber} /> : null}
                    {order.trackingUrl ? <ShareTracking trackingUrl={order.trackingUrl} /> : null}
                    <Otp pickupOtp={order.pickupOtp} dropOtp={order.dropOtp} />
                </>}
                {order.pickupProof || order.deliveryProof ? <Attachments images={[order.pickupProof, order.deliveryProof]} /> : null}
                <OrderDetails category={order.orderCategory} orderId={order.clientOrderId} amount={order.orderAmount} />
                {orderStatus[order.orderState] >= 2 ? <PaymentDetails deliveryFee={order.deliveryFee} taxes={(order.priceWithGST - order.deliveryFee)}
                    total={order.priceWithGST} orderId={order.orderId} /> : null}
                <HelpAndSupport orderId={order.orderId}/>
            </div>
        </> : <Loader2 className='animate-spin' />}
    </div>
}
